const PDF_MIN_SIZE = 1024;
const PDF_MAX_SIZE = 10 * 1024 * 1024;
const EOF_SCAN_BYTES = 1024;

/**
 * Read a File/Blob into a Uint8Array.
 * @param {Blob} file
 * @returns {Promise<Uint8Array>}
 */
async function readFileBytes(file) {
  if (typeof file.arrayBuffer === "function") {
    return new Uint8Array(await file.arrayBuffer());
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Require a coherent combination of body + cross-reference markers.
 * Classic PDFs use trailer/xref machinery; PDF 1.5+ may use an xref stream
 * (/Type /XRef) with startxref and no literal trailer keyword.
 * A lone token such as "obj" plus %%EOF is not enough.
 * @param {string} text
 * @returns {boolean}
 */
function hasCoherentPdfStructure(text) {
  const hasNumberedObject = /\d+\s+\d+\s+obj/.test(text);
  const hasEndObj = text.includes("endobj");
  const hasStreamPair =
    text.includes("stream") && text.includes("endstream");
  const hasTypeDict = text.includes("/Type");
  const hasStartxref = text.includes("startxref");
  const hasCrossRefPointer = text.includes("xref") || hasStartxref;
  const hasTrailer = text.includes("trailer");
  const hasXrefStreamType = /\/Type\s*\/XRef\b/.test(text);

  const hasBody =
    (hasNumberedObject && hasEndObj) || hasStreamPair || hasTypeDict;

  const hasClassicCrossRef = hasCrossRefPointer && hasTrailer;
  const hasModernXrefStream = hasXrefStreamType && hasStartxref;

  return hasBody && (hasClassicCrossRef || hasModernXrefStream);
}

/**
 * Validate merchant PDF file bytes with bounded structural checks.
 * @param {Uint8Array} bytes
 * @param {string} label
 */
function validateMerchantPdfBytes(bytes, label) {
  if (!bytes || bytes.length < PDF_MIN_SIZE) {
    throw new Error(`${label} file is too small to be a valid document`);
  }

  if (bytes.length > PDF_MAX_SIZE) {
    throw new Error(`${label} file exceeds the 10MB size limit`);
  }

  const isPDF =
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d;

  if (!isPDF) {
    throw new Error(`${label} must be a PDF document`);
  }

  const text = Array.prototype.map
    .call(bytes, (byte) => String.fromCharCode(byte))
    .join("");

  if (!hasCoherentPdfStructure(text)) {
    throw new Error(`${label} appears to be an incomplete or corrupt PDF`);
  }

  const scanStart = Math.max(0, text.length - EOF_SCAN_BYTES);
  const tail = text.slice(scanStart);
  if (!tail.includes("%%EOF")) {
    throw new Error(`${label} appears to be an incomplete or corrupt PDF`);
  }
}

/**
 * Validate an optional/required merchant PDF File.
 * @param {File|Blob|null|undefined} file
 * @param {string} label
 * @param {{ required?: boolean }} [options]
 * @returns {Promise<void>}
 */
export async function validateMerchantPdfFile(file, label, options = {}) {
  const { required = true } = options;

  if (!file) {
    if (!required) return;
    throw new Error(`${label} document is missing`);
  }

  if (typeof file.size === "number" && file.size === 0) {
    throw new Error(`${label} file is too small to be a valid document`);
  }

  const bytes = await readFileBytes(file);
  validateMerchantPdfBytes(bytes, label);
}

export const HAWKERS_PDF_ACCEPT = "application/pdf";

export { PDF_MIN_SIZE, PDF_MAX_SIZE, validateMerchantPdfBytes };
